import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import CustomInputText from "../CustomInput/CustomInputText";
import CustomDropdown from "../CustomInput/CustomDropdown";
import { BodyText, DangerText } from "../../styles/Typography";
import {
  IconId,
  IconCase,
  IconCompany,
  IconLink,
  IconUser,
  IconLock,
} from "../../assets/icons/Icon";
import {
  CreateProjectInstanceData,
  EditProjectInstanceData,
  InstanceClient,
  InstanceClientData,
  InstanceClientKind,
  ProjectInstance,
  ProjectRole,
} from "../../store/types";
import { Column, SmallColumn } from "./Dialogs.styles";
import {
  FieldLabel,
  Hint,
  SecretBox,
  SecretValue,
  KindSelect,
} from "./AccessDialogs.styles";

type Option = { label: string; value: any };

/* ---------- общий каркас ---------- */

interface FormDialogProps {
  visible: boolean;
  header: string;
  submitLabel: string;
  onHide: () => void;
  /** true — закрыть диалог */
  onSubmit: () => Promise<boolean>;
  danger?: boolean;
  width?: string;
  children: React.ReactNode;
}

const FormDialog: React.FC<FormDialogProps> = ({
  visible,
  header,
  submitLabel,
  onHide,
  onSubmit,
  danger,
  width = "520px",
  children,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (await onSubmit()) onHide();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width, maxWidth: "95vw" }}
      onHide={onHide}
      footer={
        <div>
          <Button label="Отмена" onClick={onHide} outlined />
          <Button
            label={submitLabel}
            onClick={submit}
            loading={submitting}
            severity={danger ? "danger" : undefined}
          />
        </div>
      }
    >
      {children}
    </Dialog>
  );
};

/** Подтверждение необратимого действия. */
export const ConfirmDialog: React.FC<{
  visible: boolean;
  header: string;
  text: React.ReactNode;
  confirmLabel: string;
  onHide: () => void;
  onConfirm: () => Promise<boolean>;
}> = ({ visible, header, text, confirmLabel, onHide, onConfirm }) => (
  <FormDialog
    visible={visible}
    header={header}
    submitLabel={confirmLabel}
    onHide={onHide}
    onSubmit={onConfirm}
    danger
  >
    <Column>{typeof text === "string" ? <DangerText>{text}</DangerText> : text}</Column>
  </FormDialog>
);

/* ---------- роль продукта ---------- */

const ROLE_NAME = /^[a-z][a-z0-9_]*$/;

/** Создание роли (имя + описание) или изменение описания (имя неизменно). */
export const RoleDialog: React.FC<{
  visible: boolean;
  role?: ProjectRole | null;
  onHide: () => void;
  onCreate: (name: string, description: string) => Promise<boolean>;
  onUpdate: (role: ProjectRole, description: string) => Promise<boolean>;
}> = ({ visible, role, onHide, onCreate, onUpdate }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameInvalid, setNameInvalid] = useState(false);

  useEffect(() => {
    if (visible) {
      setName(role?.name ?? "");
      setDescription(role?.description ?? "");
      setNameInvalid(false);
    }
  }, [visible, role]);

  const submit = async () => {
    if (role) return onUpdate(role, description.trim());

    if (!ROLE_NAME.test(name.trim())) {
      setNameInvalid(true);
      return false;
    }
    return onCreate(name.trim(), description.trim());
  };

  return (
    <FormDialog
      visible={visible}
      header={role ? "Изменить роль" : "Новая роль"}
      submitLabel={role ? "Сохранить" : "Создать"}
      onHide={onHide}
      onSubmit={submit}
    >
      <Column>
        <SmallColumn>
          <CustomInputText
            value={name}
            setValue={setName}
            onBlur={() => setNameInvalid(!!name && !ROLE_NAME.test(name.trim()))}
            placeholder="Имя роли, например moderator"
            icon={<IconId size={20} />}
            invalid={nameInvalid}
            disabled={!!role}
          />
          <Hint>
            {role
              ? "Имя роли попадает в токены и не меняется: приложения сопоставляют его со своими правами."
              : "Строчные латинские буквы, цифры и _, начинается с буквы. После создания не меняется."}
          </Hint>
        </SmallColumn>
        <InputTextarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Описание"
          rows={3}
          autoResize
        />
      </Column>
    </FormDialog>
  );
};

/* ---------- инстанс ---------- */

const SLUG = /^[a-z0-9][a-z0-9_-]*$/;
const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};

/**
 * Создание инстанса (продукт или компания могут быть зафиксированы страницей) или
 * изменение названия и адреса существующего.
 */
export const InstanceDialog: React.FC<{
  visible: boolean;
  instance?: ProjectInstance | null;
  fixedProjectId?: string;
  fixedOrganizationId?: number;
  projectsOptions?: Option[];
  companiesOptions?: Option[];
  onHide: () => void;
  onCreate?: (data: CreateProjectInstanceData) => Promise<boolean>;
  onUpdate?: (data: EditProjectInstanceData) => Promise<boolean>;
}> = ({
  visible,
  instance,
  fixedProjectId,
  fixedOrganizationId,
  projectsOptions = [],
  companiesOptions = [],
  onHide,
  onCreate,
  onUpdate,
}) => {
  const [data, setData] = useState<CreateProjectInstanceData>({
    projectId: "",
    slug: "",
    organizationId: null,
    description: "",
    baseUrl: "",
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (visible) {
      setData({
        projectId: instance?.projectId ?? fixedProjectId ?? "",
        slug: instance ? instance.id.split("@")[1] : "",
        organizationId: instance?.organizationId ?? fixedOrganizationId ?? null,
        description: instance?.description ?? "",
        baseUrl: instance?.baseUrl ?? "",
      });
      setErrors({});
    }
  }, [visible, instance, fixedProjectId, fixedOrganizationId]);

  const set = (field: keyof CreateProjectInstanceData, value: any) =>
    setData((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const next = {
      projectId: !instance && !data.projectId,
      slug: !instance && !SLUG.test(data.slug.trim()),
      organizationId: !instance && !data.organizationId,
      baseUrl: !!data.baseUrl.trim() && !isHttpUrl(data.baseUrl.trim()),
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const submit = async () => {
    if (!validate()) return false;

    if (instance)
      return onUpdate!({ description: data.description.trim(), baseUrl: data.baseUrl.trim() });

    return onCreate!({
      ...data,
      slug: data.slug.trim(),
      description: data.description.trim(),
      baseUrl: data.baseUrl.trim(),
    });
  };

  const previewId = data.projectId && data.slug ? `${data.projectId}@${data.slug.trim()}` : "";

  return (
    <FormDialog
      visible={visible}
      header={instance ? "Изменить инстанс" : "Новый инстанс"}
      submitLabel={instance ? "Сохранить" : "Создать"}
      onHide={onHide}
      onSubmit={submit}
    >
      <Column>
        {!instance && !fixedProjectId && (
          <CustomDropdown
            value={data.projectId}
            onChange={(v) => set("projectId", v)}
            options={projectsOptions}
            placeholder="Продукт"
            icon={<IconCase size={20} />}
            invalid={errors.projectId}
            filter
          />
        )}
        {!instance && !fixedOrganizationId && (
          <CustomDropdown
            value={data.organizationId}
            onChange={(v) => set("organizationId", v)}
            options={companiesOptions}
            placeholder="Компания"
            icon={<IconCompany size={20} />}
            invalid={errors.organizationId}
            filter
          />
        )}
        {!instance && (
          <SmallColumn>
            <CustomInputText
              value={data.slug}
              setValue={(v) => set("slug", v)}
              placeholder="Короткое имя (slug), например company_a"
              icon={<IconId size={20} />}
              invalid={errors.slug}
            />
            <Hint>
              {previewId
                ? `Id инстанса: ${previewId}. После создания не меняется.`
                : "Строчные латинские буквы, цифры, _ и -. Id инстанса: {продукт}@{slug}."}
            </Hint>
          </SmallColumn>
        )}
        <SmallColumn>
          <CustomInputText
            value={data.baseUrl}
            setValue={(v) => set("baseUrl", v)}
            placeholder="Адрес сервера (необязательно)"
            icon={<IconLink size={20} />}
            invalid={errors.baseUrl}
          />
          <Hint>Например, https://cemtest.company-a.local</Hint>
        </SmallColumn>
        <InputTextarea
          value={data.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Описание (необязательно)"
          rows={3}
          autoResize
        />
      </Column>
    </FormDialog>
  );
};

/* ---------- клиент инстанса ---------- */

export const CLIENT_KIND_LABELS: Record<InstanceClientKind, string> = {
  web: "Веб с бэкендом",
  spa: "SPA без бэкенда",
  service: "Только сервис",
};

const KIND_HINTS: Record<InstanceClientKind, string> = {
  web:
    "Бэкенд приложения входит от имени пользователей (BFF) и получает фид изменений. Есть секрет.",
  spa: "Браузерное приложение без бэкенда: вход с PKCE, без секрета и без фида изменений.",
  service:
    "Бэкенд без собственного входа (например, у десктопа): только фид изменений. Есть секрет.",
};

const toLines = (value: string) =>
  value
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

/** Создание клиента (вид + адреса) или изменение адресов существующего. */
export const ClientDialog: React.FC<{
  visible: boolean;
  client?: InstanceClient | null;
  baseUrl?: string | null;
  onHide: () => void;
  onCreate: (data: InstanceClientData) => Promise<boolean>;
  onUpdate: (redirectUris: string[], postLogoutRedirectUris: string[]) => Promise<boolean>;
}> = ({ visible, client, baseUrl, onHide, onCreate, onUpdate }) => {
  const [kind, setKind] = useState<InstanceClientKind>("web");
  const [redirects, setRedirects] = useState("");
  const [postLogout, setPostLogout] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!visible) return;

    const base = (baseUrl ?? "").replace(/\/+$/, "");
    setKind(client?.kind ?? "web");
    setRedirects(client ? client.redirectUris.join("\n") : base ? `${base}/signin-oidc` : "");
    setPostLogout(
      client ? client.postLogoutRedirectUris.join("\n") : base ? `${base}/signout-callback-oidc` : ""
    );
    setError("");
  }, [visible, client, baseUrl]);

  const submit = async () => {
    const redirectUris = kind === "service" ? [] : toLines(redirects);
    const postLogoutRedirectUris = kind === "service" ? [] : toLines(postLogout);

    const invalid = [...redirectUris, ...postLogoutRedirectUris].find(
      (x) => !isHttpUrl(x) || x.includes("#")
    );
    if (invalid) {
      setError(`Адрес «${invalid}» должен быть абсолютным http(s)-адресом без #.`);
      return false;
    }
    if (kind !== "service" && redirectUris.length === 0) {
      setError("Нужен хотя бы один адрес возврата после входа.");
      return false;
    }

    setError("");
    return client
      ? onUpdate(redirectUris, postLogoutRedirectUris)
      : onCreate({ kind, redirectUris, postLogoutRedirectUris });
  };

  return (
    <FormDialog
      visible={visible}
      header={client ? "Адреса клиента" : "Новый клиент инстанса"}
      submitLabel={client ? "Сохранить" : "Создать"}
      onHide={onHide}
      onSubmit={submit}
      width="600px"
    >
      <Column>
        {error && <DangerText>{error}</DangerText>}
        {!client && (
          <SmallColumn>
            <KindSelect
              value={kind}
              onChange={(e) => e.value && setKind(e.value)}
              options={(Object.keys(CLIENT_KIND_LABELS) as InstanceClientKind[]).map((k) => ({
                label: CLIENT_KIND_LABELS[k],
                value: k,
              }))}
            />
            <Hint>{KIND_HINTS[kind]}</Hint>
          </SmallColumn>
        )}
        {kind !== "service" && (
          <>
            <SmallColumn>
              <FieldLabel>Адреса возврата после входа (по одному в строке)</FieldLabel>
              <InputTextarea
                value={redirects}
                onChange={(e) => setRedirects(e.target.value)}
                rows={2}
                autoResize
              />
              <Hint>Для ASP.NET Core: https://&lt;сервер&gt;/signin-oidc</Hint>
            </SmallColumn>
            <SmallColumn>
              <FieldLabel>Адреса возврата после выхода (необязательно)</FieldLabel>
              <InputTextarea
                value={postLogout}
                onChange={(e) => setPostLogout(e.target.value)}
                rows={2}
                autoResize
              />
              <Hint>Для ASP.NET Core: https://&lt;сервер&gt;/signout-callback-oidc</Hint>
            </SmallColumn>
          </>
        )}
      </Column>
    </FormDialog>
  );
};

/**
 * Показ секрета клиента. Сервер хранит только хэш — повторно секрет не получить,
 * поэтому диалог закрывается только явной кнопкой.
 */
export const SecretDialog: React.FC<{
  client: InstanceClient | null;
  onHide: () => void;
}> = ({ client, onHide }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => setCopied(false), [client]);

  const copy = async () => {
    if (!client?.clientSecret) return;
    await navigator.clipboard.writeText(client.clientSecret);
    setCopied(true);
  };

  return (
    <Dialog
      header="Секрет клиента"
      visible={!!client}
      style={{ width: "600px", maxWidth: "95vw" }}
      onHide={onHide}
      closable={false}
      closeOnEscape={false}
      footer={<Button label="Я сохранил секрет" onClick={onHide} />}
    >
      <Column>
        <DangerText>
          Секрет показывается один раз. Сохраните его сейчас и передайте администратору
          сервера компании — восстановить его нельзя, только выпустить новый.
        </DangerText>
        <SmallColumn>
          <FieldLabel>client_id</FieldLabel>
          <SecretBox>
            <SecretValue>{client?.clientId}</SecretValue>
          </SecretBox>
        </SmallColumn>
        <SmallColumn>
          <FieldLabel>client_secret</FieldLabel>
          <SecretBox>
            <SecretValue>{client?.clientSecret}</SecretValue>
            <Button
              icon={copied ? "pi pi-check" : "pi pi-copy"}
              text
              onClick={copy}
              tooltip={copied ? "Скопировано" : "Скопировать"}
            />
          </SecretBox>
        </SmallColumn>
        <BodyText>
          В конфигурации сервера: <code>Sso:ClientId</code> и <code>Sso:ClientSecret</code>.
        </BodyText>
      </Column>
    </Dialog>
  );
};

/* ---------- назначение ролей ---------- */

/** Роль пользователю на одном инстансе. */
export const GrantRoleDialog: React.FC<{
  visible: boolean;
  usersOptions: Option[];
  rolesOptions: Option[];
  onHide: () => void;
  onGrant: (userId: string, roleId: string) => Promise<boolean>;
}> = ({ visible, usersOptions, rolesOptions, onHide, onGrant }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (visible) {
      setUserId(null);
      setRoleId(null);
      setInvalid(false);
    }
  }, [visible]);

  const submit = async () => {
    if (!userId || !roleId) {
      setInvalid(true);
      return false;
    }
    return onGrant(userId, roleId);
  };

  return (
    <FormDialog visible={visible} header="Назначить роль" submitLabel="Назначить" onHide={onHide} onSubmit={submit}>
      <Column>
        {rolesOptions.length === 0 && (
          <DangerText>В каталоге продукта нет ролей — сначала создайте их на странице продукта.</DangerText>
        )}
        <CustomDropdown
          value={userId}
          onChange={setUserId}
          options={usersOptions}
          placeholder="Пользователь"
          icon={<IconUser size={20} />}
          invalid={invalid && !userId}
          filter
        />
        <CustomDropdown
          value={roleId}
          onChange={setRoleId}
          options={rolesOptions}
          placeholder="Роль"
          icon={<IconLock size={20} />}
          invalid={invalid && !roleId}
        />
      </Column>
    </FormDialog>
  );
};

/** Роль пользователю на всех инстансах продукта — назначить или снять. */
export const BulkRoleDialog: React.FC<{
  visible: boolean;
  mode: "grant" | "revoke";
  role: ProjectRole | null;
  usersOptions: Option[];
  onHide: () => void;
  onSubmit: (userId: string) => Promise<boolean>;
}> = ({ visible, mode, role, usersOptions, onHide, onSubmit }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (visible) {
      setUserId(null);
      setInvalid(false);
    }
  }, [visible]);

  const submit = async () => {
    if (!userId) {
      setInvalid(true);
      return false;
    }
    return onSubmit(userId);
  };

  return (
    <FormDialog
      visible={visible}
      header={mode === "grant" ? "Назначить на все инстансы" : "Снять со всех инстансов"}
      submitLabel={mode === "grant" ? "Назначить" : "Снять"}
      danger={mode === "revoke"}
      onHide={onHide}
      onSubmit={submit}
    >
      <Column>
        <BodyText>
          {mode === "grant"
            ? `Роль «${role?.name}» будет назначена пользователю на всех существующих инстансах продукта. Инстансы, созданные позже, её не получат.`
            : `Роль «${role?.name}» будет снята с пользователя на всех инстансах продукта.`}
        </BodyText>
        <CustomDropdown
          value={userId}
          onChange={setUserId}
          options={usersOptions}
          placeholder="Пользователь"
          icon={<IconUser size={20} />}
          invalid={invalid && !userId}
          filter
        />
      </Column>
    </FormDialog>
  );
};
